import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { anchoredOffCentre, check, repeatsAClass, tweenCalls, valuesOf } from '../skillUsage';

function endScales(script: string): number[] {
    return tweenCalls(script).flatMap(({ method, chunk }) => {
        const end =
            method === 'to'
                ? chunk
                : method === 'fromTo'
                  ? chunk.slice(chunk.indexOf('}') + 1)
                  : '';
        return [...valuesOf(end, 'scaleY'), ...valuesOf(end, 'scale')];
    });
}

export const glyphGrowStretchSkill: CodeoSkill = {
    name: SKILL_NAMES.glyphGrowStretch,
    kind: 'codeo',
    description:
        'Make a word do what it says about size: its letters spring up from a flat strip on the ' +
        'baseline, overshoot far taller than type should be, and settle — or the whole word swells ' +
        'in steps until it outgrows the frame. For a word about growing, stretching, rising, ' +
        'expanding or getting bigger.',
    checkLooksFor:
        'per-letter spans scaled on scaleY (or scale) past 1.3, or squashed to 0.7 or below, from a transform origin on their baseline',
    documentUsesSkill: check(
        (script, markup, html) =>
            repeatsAClass(markup, 'span') &&
            ([...valuesOf(script, 'scaleY'), ...valuesOf(script, 'scale')].some(
                (value) => value >= 1.3,
            ) ||
                endScales(script).some((value) => value <= 0.7)) &&
            anchoredOffCentre(html),
    ),
    reviewNote:
        'Letters drawn far taller or wider than the font draws them, at the peak of the stretch, are ' +
        'this technique working: the distortion is the word acting out its meaning.',
    instructions: `# Glyph Grow & Stretch

The word performs its own meaning about size. **Stretch**: the letters start as a flat strip lying on the baseline, spring up one after another to about half again their height, and settle back to their true shape. **Grow**: the whole word swells in steps, each one bigger, until it is too big for the frame. Either way the baseline never moves: everything grows **up from the floor**, so it reads as growth rather than as a zoom.

Use it on the one word in the line that is about size or growth: grow, stretch, rise, expand, scale, bigger, more, tall. On any other word it is a distortion for its own sake.

${KINETIC_WORD_SETTING}

## Markup

One line per word, sized to the frame by font-size (\`data-l5-allow-literal\`, since frame-filling display type is outside the type scale), each letter its own \`inline-block\` span. Set it in capitals. Leave the line room above it, about half its height, for the overshoot.

\`\`\`html
<div id="grow-beat" class="absolute inset-0 flex items-end justify-center" style="padding-bottom: 18%;">
  <div id="grow-word" data-l5-allow-literal style="line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa);">
    <span class="gs-ch">G</span><span class="gs-ch">R</span><span class="gs-ch">O</span><span class="gs-ch">W</span>
  </div>
</div>
\`\`\`

\`\`\`css
.gs-ch { display: inline-block; transform-origin: 50% 88%; }
\`\`\`

The origin sits on the baseline (about 88% down a capital's line box at \`line-height: 1\`), not the bottom of the box, so a scaled letter stays on the line.

## Timeline code — stretch

\`\`\`js
const tl = gsap.timeline({ paused: true });
const word = document.querySelector('#grow-word');
fitWord(word, 0.86);

const CUE = 0.35;                       // the word's cue
const letters = [...word.querySelectorAll('.gs-ch')];

letters.forEach((ch, i) => {
  const at = CUE - 0.06 + i * 0.016;
  // Nothing until the cue, then a flat strip on the floor, springing up past its height...
  tl.fromTo(ch, { scaleY: 0 }, { scaleY: 0.08, duration: 0.03, ease: 'none' }, at - 0.03);
  tl.to(ch, { scaleY: 1.5, duration: 0.2, ease: 'power2.inOut' }, at);
  // ...then settling to its true shape, the later letters a beat behind.
  tl.to(ch, { scaleY: 1, duration: 0.18, ease: 'power2.inOut' }, CUE + 0.26 + i * 0.008);
});

tl.set({}, {}, 1.6);
window.__timelines = { main: tl };
\`\`\`

## Timeline code — grow

Three or four steps, each on a beat of the narration or about a third of a second apart, each bigger than the last. Size the word so the **largest resting step still fits the frame**; only the exit may outgrow it, swelling past the edges as the beat hands off. Scale the whole line about its baseline centre:

\`\`\`js
gsap.set(word, { transformOrigin: '50% 88%' });
fitWord(word, 0.86 / 2.1);                                        // the biggest resting step fills the frame
const STEPS = [[0.4, 1], [0.75, 1.45], [1.05, 2.1]];               // [time, scale]
// ...and on the handoff it keeps growing, out past the edges.
tl.to(word, { scale: 6, duration: 0.25, ease: 'power3.in' }, 1.5);
tl.fromTo(word, { scale: 0.35 }, { scale: STEPS[0][1], duration: 0.16, ease: 'back.out(2.2)' }, STEPS[0][0] - 0.1);
STEPS.slice(1).forEach(([at, scale]) => {
  tl.to(word, { scale, duration: 0.16, ease: 'back.out(2.2)' }, at - 0.08);
});
\`\`\`

## Adapt

- **Taller, not wider.** Stretch only \`scaleY\`; the letters keep their width, so they read as type pulled upward, not as a zoom.
- **A rising line of letters.** For "rise" or "climb", let the peak heights rise across the word (1.2, 1.35, 1.5, 1.65…) so the word slopes upward at the top of the spring.
- **Shrinking.** The same moves in reverse for less, smaller, shrink: the word steps down in size, or the letters squash toward a strip on the baseline. Take them to 0.7 of their height or below, so the shrink reads as the word's meaning rather than a settle.

## Pitfalls

- **One mode per beat.** Stretch or grow, not both one after the other: the second reads as a correction to the first.
- **Never at rest past the frame.** A word cropped by the frame edges while it holds reads as a layout mistake. Outgrowing the frame is only ever the exit.

- **Headroom for the peak.** At the top of the stretch the letters are half again their height, so the word sits low: its baseline about 80% of the way down the frame, its top no higher than the middle. Centred, the peak runs off the top of the frame. Check the frame at the peak, not only at rest.
- **The baseline is fixed.** An origin at the centre makes the letters grow downward as well as up, and the word reads as zooming, not growing.
- **No strip waiting on the floor.** Before its cue a letter is at \`scaleY: 0\`; the flat strip appears a frame before it springs. A strip lying there for a third of a second reads as a rendering fault.
- **Fast.** Each stretch is 0.15–0.25s. A slow stretch looks like a tween; a fast one looks like the word is alive.
- **One meaning-word.** The rest of the line, if any, arrives plainly; only the word about size is stretched.
- The letters are transform-tweened, so they carry no CSS \`transform\` beyond the origin.`,
};
